import type { GetServerSidePropsContext } from 'next'
import { buildShopSeo } from '../catalog/seo'
import { parseRoute } from '../catalog/routing'
import {
  parseRuntimeHomeSettings,
  resolveRuntimeFaviconUrl,
  resolveRuntimeSiteName,
} from '../catalog/runtimeSettings'
import { fetchJson, resolveApiBase, resolveOrigin } from '../catalog/ssr'
import { loadInitialSeoData } from '../catalog/ssrSeo'
import type { ShopLocale } from '../i18n/shopLocale'
import type { CatchAllPageProps } from './types'

export const buildCatchAllPageProps = async ({
  ctx,
  initialPathname,
  initialSearch,
  initialLocale,
}: {
  ctx: GetServerSidePropsContext
  initialPathname: string
  initialSearch: string
  initialLocale: ShopLocale
}): Promise<CatchAllPageProps> => {
  const route = parseRoute(initialPathname, initialSearch)
  const siteUrl = resolveOrigin(ctx)
  const apiBase = resolveApiBase(siteUrl)
  const settingsResponse = await fetchJson(`${apiBase}/settings/public`).catch(() => ({
    ok: false,
    status: 500,
    data: {} as Record<string, unknown>,
  }))
  const initialSettings = parseRuntimeHomeSettings(settingsResponse.data?.settings)
  const initialActiveLocales = initialSettings.activeLocales
  const initialSiteName = resolveRuntimeSiteName(initialSettings, 'Ecommerce')
  const initialFaviconUrl = resolveRuntimeFaviconUrl(initialSettings, '/favicon.ico')
  const initialSeo = await loadInitialSeoData(
    route,
    apiBase,
    initialSiteName,
    initialLocale,
    settingsResponse.data,
  ).catch(() =>
    buildShopSeo({
      route,
      pageData: null,
      productData: null,
      categoryRouteData: null,
      homePageData: null,
      currentPathname: initialPathname,
      siteName: initialSiteName,
      activeLocale: initialLocale,
    }),
  )

  return {
    initialPathname,
    initialSearch,
    initialLocale,
    initialActiveLocales,
    initialSeo,
    siteUrl,
    initialSiteName,
    initialFaviconUrl,
  }
}
